import React, { useState } from 'react';
import { Rocket, Mail, Send, Github, Twitter, Linkedin } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useToast } from '../hooks/use-toast';

const Footer = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('access_key', '9aafeee9-4657-411d-9a26-cc401b318cc1');
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('message', formData.message);
      formDataToSend.append('subject', `New LaunchPilot Inquiry from ${formData.name}`);
      formDataToSend.append('from_name', 'LaunchPilot Contact Form');
      formDataToSend.append('replyto', formData.email);

      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        body: formDataToSend
      });

      const result = await response.json();

      if (result.success) {
        toast({
          title: 'Message Sent Successfully!',
          description: "We'll get back to you within 24 hours.",
        });
        setFormData({ name: '', email: '', message: '' });
      } else {
        toast({
          title: 'Error',
          description: 'Failed to send message. Please try again.',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to send message. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <footer id="contact" className="relative py-20 px-6 border-t border-white/10 bg-black">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-12 mb-16">
          {/* Contact Form */}
          <div>
            <h3 className="text-3xl font-extrabold mb-4 text-white">Let's Build Together</h3>
            <p className="text-gray-400 mb-8">Ready to turn your idea into reality? Get in touch and let's start your journey.</p>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input
                type="text"
                name="name"
                placeholder="Your Name"
                value={formData.name}
                onChange={handleChange}
                required
                className="bg-white/5 border-white/20 focus:border-white text-white placeholder:text-gray-500 backdrop-blur-sm"
              />
              <Input
                type="email"
                name="email"
                placeholder="Your Email"
                value={formData.email}
                onChange={handleChange}
                required
                className="bg-white/5 border-white/20 focus:border-white text-white placeholder:text-gray-500 backdrop-blur-sm"
              />
              <Textarea
                name="message"
                placeholder="Tell us about your project"
                value={formData.message}
                onChange={handleChange}
                required
                rows={4}
                className="bg-white/5 border-white/20 focus:border-white text-white placeholder:text-gray-500 backdrop-blur-sm"
              />
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-white hover:bg-gray-100 text-black font-bold transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
                <Send className="ml-2 w-4 h-4" />
              </Button>
            </form>
          </div>

          {/* Company Info */}
          <div>
            <div className="flex items-center space-x-3 mb-6">
              <Rocket className="w-10 h-10 text-white" />
              <span className="text-3xl font-extrabold text-white">LaunchPilot</span>
            </div>
            <p className="text-gray-400 mb-8 leading-relaxed">
              We're on a mission to help founders and entrepreneurs launch amazing products faster. From MVP to market leader, we're with you every step of the way.
            </p>
            
            <div className="space-y-4 mb-8">
              <div className="flex items-center space-x-3 text-gray-400">
                <Mail className="w-5 h-5 text-white" />
                <a href="mailto:connect@launchpilot.today" className="hover:text-white transition-colors duration-200">
                  connect@launchpilot.today
                </a>
              </div>
            </div>

            {/* Social Links */}
            <div className="flex items-center space-x-4 mb-8">
              <a
                href="https://www.linkedin.com/in/raj-vverma/"
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all duration-300 backdrop-blur-sm"
                aria-label="Twitter"
              >
                <Twitter className="w-5 h-5 text-white" />
              </a>
              <a
                href="https://www.linkedin.com/in/raj-vverma/"
                className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-all duration-300 backdrop-blur-sm"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5 text-white" />
              </a>
            </div>

            <div className="pt-8 border-t border-white/10">
              <div className="flex flex-wrap gap-6">
                <a href="#" className="text-gray-400 hover:text-white transition-colors duration-200 font-medium">
                  About
                </a>
                <a href="#pricing" className="text-gray-400 hover:text-white transition-colors duration-200 font-medium">
                  Pricing
                </a>
                <a href="#faq" className="text-gray-400 hover:text-white transition-colors duration-200 font-medium">
                  FAQ
                </a>
                <a href="#contact" className="text-gray-400 hover:text-white transition-colors duration-200 font-medium">
                  Contact
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-white/10 text-center text-gray-500 text-sm">
          <p>&copy; {new Date().getFullYear()} LaunchPilot. All rights reserved. Built with passion for founders.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
